import React, { useMemo, useState } from "react";

/**
 * O.S.T. SIP PANEL — Cost Calculator v2 (Advanced)
 * - Αυτόματη επιφάνεια εξωτερικής τοιχοποιίας = περίμετρος × ύψος
 * - Εμβαδό δαπέδου: αυτόματο από 4 τοίχους (ορθογώνιο) ή χειροκίνητο
 * - Πάνελ-δότες ΜΟΝΟ αν ύψος > 2.50 m
 * - Τοποθέτηση: 100 €/m² εμβαδού δαπέδου
 * - Τιμές 2025-10-29: WOOD 700 €/m³, MS03-12 153.25 €, MS01-12 134.50 €, MRF03-15 174.25 €
 */

function cx(...cn: Array<string | false | null | undefined>) {
  return cn.filter(Boolean).join(" ");
}
const EUR = (v: number) =>
  v.toLocaleString("el-GR", { style: "currency", currency: "EUR", maximumFractionDigits: 2 });
const round2 = (n: number) => Math.round(n * 100) / 100;

const PANEL_WIDTH = 1.25; // m
const PANEL_HEIGHT = 2.5; // m

const PRICE = {
  WOOD_M3: 700,
  MS03_12: 153.25,
  MS01_12: 134.5,
  MRF03_15: 174.25,
};

type RoofKind = "flat_non_walkable" | "flat_walkable" | "gable" | "hip";
type Wall = { id: string; length: number };

// ---------- ΥΠΟΛΟΓΙΣΜΟΙ ----------
function countExternalPanels(walls: Wall[], height: number) {
  const rows = walls.reduce(
    (acc, w) => acc + Math.ceil((Math.max(0, w.length) || 0) / PANEL_WIDTH),
    0
  );
  const needsCordoni = height > PANEL_HEIGHT; // ΜΟΝΟ πάνω από 2.50 m
  const donors = needsCordoni ? Math.ceil(rows / 4) : 0; // 4 λωρίδες/δότη
  const fullPanels = rows + donors;
  return { rows, donors, fullPanels };
}

function woodForRoofPer100m2(kind: RoofKind) {
  switch (kind) {
    case "flat_non_walkable":
      return 1.89;
    case "flat_walkable":
      return 3.15;
    case "gable":
      return 2.35;
    case "hip":
      return 2.55;
  }
}

function panelCountForRoof(kind: RoofKind, areaM2: number) {
  const factor = Math.max(areaM2, 0) / 100;
  switch (kind) {
    case "flat_non_walkable":
    case "flat_walkable":
      return Math.max(1, Math.ceil(32 * factor));
    case "gable":
      return Math.max(1, Math.ceil(36 * factor));
    case "hip":
      return Math.max(1, Math.ceil(38 * factor));
  }
}

function panelTypeForRoof(kind: RoofKind) {
  if (kind === "flat_walkable") return { code: "MRF03-15", price: PRICE.MRF03_15 };
  return { code: "MS03-12", price: PRICE.MS03_12 };
}

function cuttingFee(areaM2: number, kind: RoofKind, extraDifficultyPct: number) {
  // Βάση ανά μέγεθος
  let base = 3000;
  if (areaM2 >= 80 && areaM2 <= 120) base = 5000;
  if (areaM2 > 120) base = 7000;

  // Συντελεστής τύπου στέγης
  let roofFactor = 1;
  if (kind === "flat_walkable") roofFactor = 1.15;
  if (kind === "gable" || kind === "hip") roofFactor = 1.25;

  // Extra δυσκολία
  const extra = 1 + (extraDifficultyPct || 0) / 100;

  return Math.round(base * roofFactor * extra);
}

// ΝΕΟ: Τοποθέτηση = 100 €/m² εμβαδού δαπέδου
function installCostByFloorArea(areaM2: number) {
  return Math.max(0, Math.round(areaM2 * 100));
}

function autoInternalRows(perimeter: number) {
  const length = 0.5 * perimeter;
  return Math.ceil(length / PANEL_WIDTH);
}

function uid() {
  // fallback αν λείπει το crypto.randomUUID
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return (globalThis.crypto?.randomUUID?.() as string) ?? `w_${Math.random().toString(36).slice(2, 9)}`;
}

// ---------- COMPONENT ----------
export default function OstSipCalculatorV2() {
  const [mode, setMode] = useState<"basic" | "pro">("basic");

  // Γενικά
  const [manualFloorArea, setManualFloorArea] = useState<number>(100);
  const [autoFloorArea, setAutoFloorArea] = useState<boolean>(true);
  const [roofKind, setRoofKind] = useState<RoofKind>("flat_non_walkable");
  const [includeInstall, setIncludeInstall] = useState<boolean>(false);

  // Εξωτερικοί τοίχοι
  const [wallHeight, setWallHeight] = useState<number>(3.0);
  const [walls, setWalls] = useState<Wall[]>([
    { id: uid(), length: 10 },
    { id: uid(), length: 10 },
    { id: uid(), length: 10 },
    { id: uid(), length: 10 },
  ]);

  // Εσωτερικοί (auto)
  const [enableInternalAuto, setEnableInternalAuto] = useState<boolean>(false);

  // Πρόσθετη δυσκολία (πάντα ορατή)
  const [extraDifficultyPct, setExtraDifficultyPct] = useState<number>(0);

  // Παράγωγα
  const perimeter = useMemo(() => round2(walls.reduce((a, w) => a + (w.length || 0), 0)), [walls]);

  const extWallAreaM2 = useMemo(
    () => round2(perimeter * wallHeight),
    [perimeter, wallHeight]
  );

  const derivedFloorArea = useMemo(() => {
    if (walls.length !== 4) return NaN;
    const [w1, w2, w3, w4] = walls.map((w) => Math.max(0, w.length || 0));
    const width = (w1 + w3) / 2;
    const depth = (w2 + w4) / 2;
    const area = width * depth;
    return isFinite(area) && area > 0 ? round2(area) : NaN;
  }, [walls]);

  const effectiveAuto = autoFloorArea && isFinite(derivedFloorArea);
  const effectiveFloorArea = effectiveAuto ? (derivedFloorArea as number) : manualFloorArea;

  const ext = useMemo(() => countExternalPanels(walls, wallHeight), [walls, wallHeight]);

  const internalRows = useMemo(
    () => (enableInternalAuto ? autoInternalRows(perimeter) : 0),
    [enableInternalAuto, perimeter]
  );
  const internalDonors = useMemo(
    () => (enableInternalAuto && wallHeight > PANEL_HEIGHT ? Math.ceil(internalRows / 4) : 0),
    [enableInternalAuto, internalRows, wallHeight]
  );
  const internalPanels = enableInternalAuto ? internalRows + internalDonors : 0;

  // Κόστη τοίχων
  const costExtPanels = useMemo(() => ext.fullPanels * PRICE.MS03_12, [ext.fullPanels]);
  const costIntPanels = useMemo(() => internalPanels * PRICE.MS01_12, [internalPanels]);

  // Ξυλεία
  const woodM3External = useMemo(() => round2((perimeter / 10) * 0.699), [perimeter]); // από το template σου
  const woodM3Internal = useMemo(
    () => (enableInternalAuto ? round2(((0.5 * perimeter) / 10) * 0.4) : 0),
    [enableInternalAuto, perimeter]
  );
  const costWoodWalls = useMemo(
    () => (woodM3External + woodM3Internal) * PRICE.WOOD_M3,
    [woodM3External, woodM3Internal]
  );

  // Στέγη
  const roofPanels = useMemo(
    () => panelCountForRoof(roofKind, effectiveFloorArea),
    [roofKind, effectiveFloorArea]
  );
  const roofPanelType = panelTypeForRoof(roofKind);
  const costRoofPanels = useMemo(() => roofPanels * roofPanelType.price, [roofPanels, roofPanelType.price]);
  const roofWoodM3 = useMemo(
    () => round2((woodForRoofPer100m2(roofKind) * (effectiveFloorArea / 100)) || 0),
    [roofKind, effectiveFloorArea]
  );
  const costRoofWood = useMemo(() => roofWoodM3 * PRICE.WOOD_M3, [roofWoodM3]);

  // Υπηρεσίες
  const costCutting = useMemo(
    () => cuttingFee(effectiveFloorArea, roofKind, extraDifficultyPct),
    [effectiveFloorArea, roofKind, extraDifficultyPct]
  );
  const costInstall = useMemo(() => {
    if (!includeInstall) return 0;
    return installCostByFloorArea(effectiveFloorArea);
  }, [includeInstall, effectiveFloorArea]);

  // Σύνολα
  const costKIT = useMemo(
    () =>
      round2(
        costExtPanels + costIntPanels + costWoodWalls + costRoofPanels + costRoofWood + costCutting
      ),
    [costExtPanels, costIntPanels, costWoodWalls, costRoofPanels, costRoofWood, costCutting]
  );
  const costTotal = useMemo(() => round2(costKIT + costInstall), [costKIT, costInstall]);

  const showAutoHint = autoFloorArea && !isFinite(derivedFloorArea);

  return (
    <div className="w-full max-w-6xl mx-auto p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">O.S.T. SIP — Υπολογιστής Κόστους (Advanced v2)</h1>
          <p className="text-sm text-gray-600">
            Αυτόματη επιφάνεια τοιχοποιίας, αυτόματο εμβαδό (4 τοίχοι), πάνελ-δότες μόνο αν {'>'} 2.50 m.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ModeToggle mode={mode} onChange={setMode} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* Αριστερή στήλη: Είσοδοι */}
        <section className="lg:col-span-2 space-y-4">
          <Card title="Γενικά">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Εμβαδό (auto/manual) */}
              <div className="sm:col-span-2">
                <label className="text-sm w-full block">
                  <div className="mb-1 text-gray-700 flex items-center justify-between">
                    <span>Εμβαδό δαπέδου (m²)</span>
                    <label className="text-xs flex items-center gap-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={autoFloorArea}
                        onChange={(e) => setAutoFloorArea(e.target.checked)}
                        disabled={!isFinite(derivedFloorArea) && autoFloorArea}
                        title="Αυτόματο από 4 τοίχους"
                      />
                      <span>Αυτόματο από τοίχους</span>
                    </label>
                  </div>

                  {effectiveAuto ? (
                    <div className="w-full border rounded-lg px-3 py-2 bg-gray-50 text-gray-700">
                      {derivedFloorArea} m²
                    </div>
                  ) : (
                    <input
                      className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                      type="number"
                      value={Number.isFinite(manualFloorArea) ? manualFloorArea : 0}
                      onChange={(e) => setManualFloorArea(Number(e.target.value))}
                      min={20}
                      step={1}
                    />
                  )}
                  {showAutoHint && (
                    <div className="mt-1 text-[11px] text-amber-600">
                      Για αυτόματο εμβαδό χρειάζονται ακριβώς 4 τοίχοι (ορθογώνια υπόθεση).
                    </div>
                  )}
                </label>
              </div>

              <SelectField
                label="Τύπος στέγης"
                value={roofKind}
                onChange={(v: string) => setRoofKind(v as RoofKind)}
                options={[
                  { value: "flat_non_walkable", label: "Επίπεδη — μη βατή" },
                  { value: "flat_walkable", label: "Επίπεδη — ΒΑΤΗ (MRF03-15)" },
                  { value: "gable", label: "Δίρριχτη (~15°)" },
                  { value: "hip", label: "Τετράριχτη (~15°)" },
                ]}
              />
              <ToggleField label="Συμπερίληψη τοποθέτησης" checked={includeInstall} onChange={setIncludeInstall} />
            </div>
          </Card>

          <Card title="Εξωτερικοί τοίχοι (ενιαίο ύψος)">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <NumberField
                label="Ύψος όλων (m)"
                value={wallHeight}
                onChange={setWallHeight}
                min={2.4}
                max={3.4}
                step={0.1}
              />
              <div className="sm:col-span-2 flex items-center gap-2">
                <button
                  className="px-3 py-2 text-sm border rounded-lg hover:bg-gray-50"
                  onClick={() => setWalls((w) => [...w, { id: uid(), length: 5 }])}
                >
                  + Προσθήκη τοίχου
                </button>
                <button
                  className="px-3 py-2 text-sm border rounded-lg hover:bg-gray-50"
                  onClick={() =>
                    setWalls([
                      { id: uid(), length: 10 },
                      { id: uid(), length: 10 },
                      { id: uid(), length: 10 },
                      { id: uid(), length: 10 },
                    ])
                  }
                >
                  Πρότυπο: 4 × 10 m
                </button>
              </div>
            </div>

            <div className="mt-4 space-y-2">
              {walls.map((w, i) => (
                <div key={w.id} className="flex items-center gap-3">
                  <div className="w-10 text-sm text-gray-600">Τ{i + 1}</div>
                  <NumberField
                    label="Μήκος (m)"
                    value={w.length}
                    onChange={(val: number) =>
                      setWalls((arr) => arr.map((x) => (x.id === w.id ? { ...x, length: val } : x)))
                    }
                    step={0.1}
                    min={0.5}
                  />
                  <button
                    className="ml-auto text-xs px-2 py-1 border rounded hover:bg-gray-50"
                    onClick={() => setWalls((arr) => arr.filter((x) => x.id !== w.id))}
                  >
                    Διαγραφή
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
              <Stat label="Περίμετρος" value={`${perimeter.toFixed(2)} m`} />
              <Stat label="Ύψος" value={`${wallHeight.toFixed(2)} m`} />
              <Stat label="Επιφάνεια εξωτερικών" value={`${extWallAreaM2.toFixed(2)} m²`} />
              <Stat label="Σειρές πάνελ (rows)" value={ext.rows} />
              <Stat label="Πάνελ-δότες (κορδόνια)" value={ext.donors} />
            </div>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-2 gap-3 text-sm">
              <Stat label="Σύνολο MS03-12 (εξωτερικά)" value={ext.fullPanels} />
              {enableInternalAuto && (
                <Stat label="Σύνολο MS01-12 (εσωτερικά)" value={internalPanels} />
              )}
            </div>
          </Card>

          <Card title="Εσωτερικοί τοίχοι (auto)">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-center">
              <ToggleField
                label="Ενεργό (σταυρός 4 χώρων)"
                checked={enableInternalAuto}
                onChange={setEnableInternalAuto}
              />
              <Stat label="Σειρές (rows)" value={internalRows} />
              <Stat label="Πάνελ-δότες" value={internalDonors} />
            </div>
            {enableInternalAuto && (
              <div className="mt-3 text-sm text-gray-600">
                Προσεγγιστικός κανόνας: συνολικό μήκος εσωτερικών ≈ 0.5 × περίμετρος.
              </div>
            )}
          </Card>

          <Card title="Στέγη">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <Stat label="Πάνελ στέγης" value={roofPanels} />
              <Stat label="Τύπος πάνελ" value={roofPanelType.code} />
              <Stat label="Ξυλεία στέγης" value={`${roofWoodM3.toFixed(2)} m³`} />
              <Stat label="Πάνελ/τεμ × τιμή" value={`${roofPanelType.code} × ${EUR(roofPanelType.price)}`} />
            </div>
          </Card>

          {/* ΠΑΝΤΑ ΟΡΑΤΟ: Πρόσθετη δυσκολία */}
          <Card title="Πρόσθετη δυσκολία">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <NumberField
                label="Extra δυσκολία (%)"
                value={extraDifficultyPct}
                onChange={setExtraDifficultyPct}
                min={0}
                max={30}
                step={1}
              />
              <div className="sm:col-span-2 text-sm text-gray-600 flex items-center">
                Επηρεάζει το κόστος κοπής/αρίθμησης.
              </div>
            </div>
          </Card>
        </section>

        {/* Δεξιά στήλη: Σύνοψη/Κόστος */}
        <aside className="lg:col-span-1 space-y-4">
          <Card title="Κόστη τοίχων">
            <div className="space-y-2 text-sm">
              <Line label={`MS03-12 εξωτερικά (${ext.fullPanels} τεμ)`} value={EUR(costExtPanels)} />
              {enableInternalAuto && (
                <Line label={`MS01-12 εσωτερικά (${internalPanels} τεμ)`} value={EUR(costIntPanels)} />
              )}
              <Line
                label={`Ξυλεία τοίχων (${(woodM3External + woodM3Internal).toFixed(2)} m³)`}
                value={EUR(costWoodWalls)}
              />
            </div>
          </Card>

          <Card title="Κόστη στέγης">
            <div className="space-y-2 text-sm">
              <Line label={`${roofPanelType.code} πάνελ (${roofPanels} τεμ)`} value={EUR(costRoofPanels)} />
              <Line label={`Ξυλεία στέγης (${roofWoodM3.toFixed(2)} m³)`} value={EUR(costRoofWood)} />
            </div>
          </Card>

          <Card title="Υπηρεσίες">
            <div className="space-y-2 text-sm">
              <Line label="Κοπή – Επεξεργασία – Αρίθμηση" value={EUR(costCutting)} />
              <Line
                label={includeInstall ? "Τοποθέτηση" : "Τοποθέτηση (μη επιλεγμένο)"}
                value={includeInstall ? EUR(costInstall) : "—"}
              />
            </div>
          </Card>

          <Card title="Σύνολα & Δείκτες">
            <div className="space-y-2 text-sm">
              <Line label="Σύνολο KIT" value={EUR(costKIT)} bold />
              {includeInstall && <Line label="Σύνολο με τοποθέτηση" value={EUR(costTotal)} bold />}
              <hr />
              <Line label="€/m² (KIT)" value={EUR(costKIT / Math.max(effectiveFloorArea, 1))} />
              {includeInstall && (
                <Line label="€/m² (με τοποθέτηση)" value={EUR(costTotal / Math.max(effectiveFloorArea, 1))} />
              )}
              <div className="mt-2 text-[11px] text-gray-500 leading-relaxed">
                * Δεν περιλαμβάνονται μεταφορικά, μεμβράνες/τελικές στρώσεις. <br />
                * Κανόνας κορδονιών: 4 λωρίδες × 0.50 m ανά πάνελ-δότη. <br />
                * Αυτόματο εμβαδό: ισχύει για 4 τοίχους (ορθογώνιο).
              </div>
            </div>
          </Card>
        </aside>
      </div>
    </div>
  );
}

/* --------- UI Primitives --------- */
function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border rounded-2xl p-4 shadow-sm bg-white">
      <div className="font-medium mb-3">{title}</div>
      {children}
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
  min,
  max,
  step = 0.01,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
}) {
  return (
    <label className="text-sm w-full">
      <div className="mb-1 text-gray-700">{label}</div>
      <input
        className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-200"
        type="number"
        value={Number.isFinite(value) ? value : 0}
        onChange={(e) => onChange(Number(e.target.value))}
        min={min}
        max={max}
        step={step}
      />
    </label>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="text-sm w-full">
      <div className="mb-1 text-gray-700">{label}</div>
      <select
        className="w-full border rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function ToggleField({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="text-sm flex items-center gap-2 select-none">
      <input type="checkbox" className="h-4 w-4" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="text-gray-700">{label}</span>
    </label>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="border rounded-lg px-3 py-2 bg-gray-50">
      <div className="text-[11px] text-gray-500">{label}</div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  );
}

function Line({ label, value, bold = false }: { label: string; value: React.ReactNode; bold?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <div className={cx("text-sm", bold && "font-semibold")}>{label}</div>
      <div className={cx("text-sm tabular-nums", bold && "font-semibold")}>{value}</div>
    </div>
  );
}

function ModeToggle({
  mode,
  onChange,
}: {
  mode: "basic" | "pro";
  onChange: (m: "basic" | "pro") => void;
}) {
  return (
    <div className="inline-flex border rounded-xl overflow-hidden">
      {([
        { v: "basic", label: "Basic" },
        { v: "pro", label: "Pro" },
      ] as const).map((o) => (
        <button
          key={o.v}
          onClick={() => onChange(o.v)}
          className={cx(
            "px-3 py-2 text-sm",
            mode === o.v ? "bg-indigo-600 text-white" : "bg-white hover:bg-gray-50"
          )}
        >
          {o.label.toUpperCase()}
        </button>
      ))}
    </div>
  );
}
